import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Gift, Zap, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

const PromoModal = ({ onClose }) => {
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutos em segundos
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showUrgency, setShowUrgency] = useState(false);

  // Delay para mostrar o modal (2 segundos após carregar a página)
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);

    return () => clearTimeout(showTimer);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!isVisible || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible]);

  // Mostrar urgência quando faltar 1 minuto
  useEffect(() => {
    if (timeLeft <= 60 && timeLeft > 0) {
      setShowUrgency(true);
    }
  }, [timeLeft]);

  // Formatar tempo
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return {
      minutes: mins.toString().padStart(2, '0'),
      seconds: secs.toString().padStart(2, '0'),
    };
  }, []);

  const time = formatTime(timeLeft);

  // Fechar modal com animação
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  // Navegar para registro
  const handleCTA = () => {
    // Salvar no localStorage que já viu a oferta
    localStorage.setItem('meumei_promo_seen', Date.now().toString());
    window.location.href = 'https://app.meumei.net/register';
  };

  // Verificar se já viu a oferta recentemente (24 horas)
  useEffect(() => {
    const lastSeen = localStorage.getItem('meumei_promo_seen');
    if (lastSeen) {
      const hoursSinceLastSeen = (Date.now() - parseInt(lastSeen)) / (1000 * 60 * 60);
      if (hoursSinceLastSeen < 24) {
        setIsVisible(false);
      }
    }
  }, []);

  const benefits = [
    'Emita notas fiscais ilimitadas',
    'Controle suas finanças facilmente',
    'Gere boletos DAS automáticos',
    'Acesse todas as funcionalidades PRO',
  ];

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isClosing ? 0 : 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleClose}
        >
          {/* Modal Container */}
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ 
              y: isClosing ? '100%' : 0, 
              opacity: isClosing ? 0 : 1 
            }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
          >
            {/* Gradient Background Header */}
            <div 
              className="relative pt-8 pb-6 px-6 text-center"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
              }}
            >
              {/* Sparkles Animation */}
              <motion.div
                className="absolute top-4 left-4"
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="text-yellow-300 w-6 h-6" />
              </motion.div>
              <motion.div
                className="absolute top-6 right-6"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="text-yellow-300 w-5 h-5" />
              </motion.div>

              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring' }}
                className="inline-flex items-center gap-2 bg-yellow-400 text-yellow-900 px-4 py-1.5 rounded-full text-sm font-bold mb-4"
              >
                <Gift className="w-4 h-4" />
                OFERTA ESPECIAL
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-2xl sm:text-3xl font-bold text-white mb-2"
              >
                🎉 7 Dias Grátis!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-white/90 text-base sm:text-lg"
              >
                Teste todas as funcionalidades PRO
              </motion.p>
            </div>

            {/* Countdown Timer */}
            <div className={`py-4 px-6 ${showUrgency ? 'bg-red-50' : 'bg-amber-50'} border-b`}>
              <div className="flex items-center justify-center gap-3">
                <motion.div
                  animate={showUrgency ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: showUrgency ? Infinity : 0 }}
                >
                  <Clock className={`w-5 h-5 ${showUrgency ? 'text-red-500' : 'text-amber-600'}`} />
                </motion.div>
                <span className={`text-sm font-medium ${showUrgency ? 'text-red-600' : 'text-amber-700'}`}>
                  {timeLeft > 0 ? 'Oferta expira em:' : 'Oferta expirada!'}
                </span>
              </div>
              
              {timeLeft > 0 && (
                <div className="flex items-center justify-center gap-2 mt-3">
                  {/* Minutes */}
                  <motion.div
                    key={time.minutes}
                    initial={{ scale: 1.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex flex-col items-center justify-center ${
                      showUrgency ? 'bg-red-500' : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                    }`}
                    style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}
                  >
                    <span className="text-2xl sm:text-3xl font-bold text-white">{time.minutes}</span>
                    <span className="text-xs text-white/80">min</span>
                  </motion.div>

                  <span className={`text-2xl font-bold ${showUrgency ? 'text-red-500' : 'text-emerald-600'}`}>:</span>

                  {/* Seconds */}
                  <motion.div
                    key={time.seconds}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex flex-col items-center justify-center ${
                      showUrgency ? 'bg-red-500' : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                    }`}
                    style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}
                  >
                    <span className="text-2xl sm:text-3xl font-bold text-white">{time.seconds}</span>
                    <span className="text-xs text-white/80">seg</span>
                  </motion.div>
                </div>
              )}

              {showUrgency && timeLeft > 0 && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-red-600 text-sm font-medium mt-2"
                >
                  ⚡ Últimos segundos! Não perca!
                </motion.p>
              )}
            </div>

            {/* Benefits List */}
            <div className="px-6 py-5">
              <p className="text-gray-600 text-sm mb-4 text-center">
                O que você terá acesso:
              </p>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-gray-700 text-sm sm:text-base">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* CTA Section */}
            <div className="px-6 pb-6 pt-2">
              {/* Main CTA Button */}
              <motion.button
                onClick={handleCTA}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={timeLeft === 0}
                className={`w-full py-4 px-6 rounded-2xl font-bold text-lg text-white flex items-center justify-center gap-2 transition-all ${
                  timeLeft === 0 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                }`}
                style={timeLeft > 0 ? { 
                  boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)' 
                } : {}}
              >
                <Zap className="w-5 h-5" />
                {timeLeft > 0 ? 'QUERO MEUS 7 DIAS GRÁTIS!' : 'Oferta Expirada'}
                {timeLeft > 0 && <ArrowRight className="w-5 h-5" />}
              </motion.button>

              {/* Secondary Option */}
              <div className="mt-4 text-center">
                <p className="text-gray-500 text-xs mb-2">Ainda não tem MEI?</p>
                <motion.a
                  href="https://app.meumei.net/abrir-mei"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm hover:text-emerald-700 transition-colors"
                >
                  Abra seu MEI conosco e ganhe 7 dias grátis
                  <ArrowRight className="w-4 h-4" />
                </motion.a>
              </div>

              {/* Trust Badge */}
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-center gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 border-2 border-white flex items-center justify-center"
                    >
                      <span className="text-white text-xs font-bold">
                        {['M', 'E', 'I', '💚'][i - 1]}
                      </span>
                    </div>
                  ))}
                </div>
                <span className="text-gray-600 text-xs">
                  <strong>+10.000</strong> MEIs já confiam em nós
                </span>
              </div>

              {/* No Credit Card */}
              <p className="text-center text-gray-400 text-xs mt-3">
                🔒 Sem cartão de crédito • Cancele quando quiser
              </p>
            </div>

            {/* Bottom safe area for mobile */}
            <div className="h-safe sm:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromoModal;